import React, { useEffect, useCallback, useRef, useState } from "react";
import mixpanel from "mixpanel-browser";
import { useDispatch, useSelector } from "react-redux";
import { FormikProps } from "formik";
import ContentContainer from "../../../templates/content-container/contentContainer";
import XGSToggle from "../../../ui-components/xgs-toggle/xgs-toggle";
import PickupMap from "./pickupMap";
import DriversList from "./driversList";
import PickupList from "./pickupList";
import PickupListFilters from "./pickupListFilters";
import PickupDetails from "../../common/pickup-details/pickupDetails";
import Collapsible from "../../../ui-components/collapsible/collapsible";
import StopsLegend from "../../../ui-components/map/stops-legend/stopsLegend";
import Legend from "../../../ui-components/molecules/legend/legend";
import { PickupStatus } from "../../../app/data/common/route";
import { userSelector } from "../../../slices/user/userSlice";
import { pickupAssignmentSelector, reloadPickupAssignmentPages, updatePickup } from "../../../slices/pickup/pickupAssignmentSlice";
import { getPickupById, getPickupDetails, isGroup } from "../../../services/pickups";
import useWindowSize from "../../../hooks/useWindowsSize";
import { getHeightBeforeEndOfPage } from "../../../hooks/utils";
import { PickupsResponseContentModel, UpdatePickupModel } from "../../../app/data/pickup/models";
import { PAGE_IDS } from "../../../app/data/common/pageIds";
import PickupEdit from "./pickupEdit";
import { getPickup, pickupDetailsSelector, setPickup } from "../../../slices/pickup/pickupDetailsSlice";

import "./pickupManagement.scss";

const mobileWidth = 768;

const PickupAssignment: React.FC<{}> = (props) => {
  const dispatch = useDispatch();
  const userState = useSelector(userSelector);
  const pickupAssignmentState = useSelector(pickupAssignmentSelector);
  const pickupDetailsState = useSelector(pickupDetailsSelector);
  const [selectedPickupIds, setSelectedPickupIds] = useState<string[]>([]);
  const [selectedPickupGroupId, setSelectedPickupGroupId] = useState("");
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);
  const [scrollToDriverId, setScrollToDriverId] = useState<number | null>(null);
  const [statuses, setStatuses] = useState([PickupStatus.UNASSIGNED, PickupStatus.ASSIGNED]);
  const [expiredOnly, setExpiredOnly] = useState(false);
  const [showFuture, setShowFuture] = useState(false);
  const [pickupSearch, setPickupSearch] = useState("");
  const [pickupDetailsNumber, setPickupDetailsNumber] = useState<string>("");
  const [showAllRoutes, setShowAllRoutes] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [dispatchToolsOpen, setDispatchToolsOpen] = useState(false);
  const [pickupsListHeight, setPickupsListHeight] = useState(0);
  const [pickupEditMode, setPickupEditMode] = useState(false);
  const [forceResetStatuses, setForceResetStatuses] = useState(false);
  const [windowWidth, windowHeight] = useWindowSize();
  const filterFormRef = useRef<FormikProps<any>>(null);

  const pickupDetails = pickupDetailsState.pickup && getPickupDetails(pickupDetailsState.pickup);
  
  useEffect(() => {
    mixpanel.track("page_viewed", { page: "pickup_management" });
  }, [userState.activeTerminal?.id]);


  const clearSelectedPickup = () => {
    setSelectedPickupIds([]);
    setPickupDetailsNumber("");
    setSelectedDriverId(null);
    setScrollToDriverId(null);
  };

  const changeRoutesMode = (showAll: boolean) => {
    if (showAll) {
      setSelectedDriverId(null);
    }
    setShowAllRoutes(showAll);
  }

  const selectDriver = (id: number | null) => {
    if (id) {
      setShowAllRoutes(false);
    }
    setSelectedDriverId(id);
  }

  const showPickupDetails = useCallback((pickupId: string) => {
    const pickupObj = getPickupById(pickupAssignmentState.pickups, pickupId);
    const pickupNumber = pickupObj?.pickup.pickupNumber;
    if (pickupDetailsNumber !== pickupNumber) {
      setPickupDetailsNumber(pickupNumber);
      dispatch(getPickup(pickupNumber));
    }    
  }, [dispatch, pickupAssignmentState.pickups, pickupDetailsNumber]);

  const onUpdatePickup = (pickup: UpdatePickupModel) => {
    dispatch(updatePickup(pickup));
    dispatch(getPickup(pickupDetailsNumber));
  }

  const onReschedulePickup = () => {
    dispatch(reloadPickupAssignmentPages(userState.activeTerminal?.id || null, { statuses, expiredOnly, showFuture, search: pickupSearch }));
    dispatch(getPickup(pickupDetailsNumber));
  }

  const onEditPickup = (pickup: PickupsResponseContentModel) => {
    dispatch(reloadPickupAssignmentPages(userState.activeTerminal?.id || null, { statuses, expiredOnly, showFuture, search: pickupSearch }));
    dispatch(setPickup(pickup));
    setPickupEditMode(false);
  }

  const showPickup = (pickupNumber: string) => {
    setForceResetStatuses(true);
    filterFormRef.current?.setFieldValue("pickupSearch", pickupNumber);
    filterFormRef.current?.submitForm();
  }

  const onClickEditButton = () => {
    setPickupEditMode(true);
  }

  useEffect(() => {
    clearSelectedPickup();
  }, [userState.activeTerminal]);

  useEffect(() => {
    if (!pickupAssignmentState.createdGroupId) return;
    const isGroupShown = pickupAssignmentState.pickups.findIndex(obj => (isGroup(obj) && obj.id === pickupAssignmentState.createdGroupId)) >= 0;
    setSelectedPickupGroupId(isGroupShown ? pickupAssignmentState.createdGroupId : "");
  }, [pickupAssignmentState.createdGroupId, pickupAssignmentState.pickups]);

  useEffect(() => {
    const fixedMobileHeight = 400;
    const height = getHeightBeforeEndOfPage(".xgs-pickup-assignment__orders",
      240,
      windowHeight,
      0);
    setPickupsListHeight(windowWidth > mobileWidth ? Math.max(height, fixedMobileHeight) : fixedMobileHeight);
  }, [windowWidth, windowHeight, dispatchToolsOpen]);

  return (
    <ContentContainer pageId={PAGE_IDS.PICKUP_ASSIGNMENT} title="Pickup Assignment" mods={{ 'full-width': true }}>
      <div className="xgs-pickup-assignment">
        <div className="xgs-pickup-assignment__row">
          <Collapsible 
          open={dispatchToolsOpen}
          onClick={() => setDispatchToolsOpen(!dispatchToolsOpen)}
          displayLabel={true}
          mods={{ "full-size": true }} 
          header={(
            <div className="xgs-pickup-assignment__collapsible-block-header">
              <div className="xgs-pickup-assignment__block-title">Dispatch Tools</div>
              {dispatchToolsOpen && (<div className="xgs-pickup-assignment__collapsible-block-header-controls">
                <div className="xgs-pickup-assignment__map-legend">
                  <Legend position={windowWidth <= mobileWidth ? "left" : "right"}>
                    <StopsLegend />
                  </Legend>
                  <div className="xgs-pickup-assignment__map-legend-title">Map Legend</div>
                </div>
                <XGSToggle
                  onChange={changeRoutesMode}
                  checked={showAllRoutes}
                  label="Show all routes"
                />
              </div>)}
            </div>
          )}>
            <div className="xgs-pickup-assignment__dispatch-tools">
              <div className="xgs-pickup-assignment__map">
                <PickupMap
                  pickupIds={selectedPickupIds}
                  driverId={selectedDriverId}
                  showAllRoutes={showAllRoutes}
                  onDriverSelect={(driverId) => {
                    selectDriver(driverId);
                    setScrollToDriverId(driverId);
                  }}
                />
              </div>
              <div className="xgs-pickup-assignment__drivers">
                <DriversList
                  pickupIds={selectedPickupIds}
                  groupId={selectedPickupGroupId}
                  selectedDriverId={selectedDriverId}
                  scrollToDriverId={scrollToDriverId}
                  onDriverSelect={(driverId) => selectDriver(driverId)}
                  onClickReschedule={() => {
                    showPickupDetails(selectedPickupIds[0]);
                    setShowReschedule(true);
                  }}
                />
              </div>
            </div>
          </Collapsible>
        </div>
        <div className="xgs-pickup-assignment__pickups">
          <div className="xgs-pickup-assignment__block-header">
            <PickupListFilters
              formRef={filterFormRef}
              onSetExpiredOnly={(v) => {
                setExpiredOnly(v);
                clearSelectedPickup();
              }}
              onSetShowFuture={(v) => {
                setShowFuture(v);
                clearSelectedPickup();
              }}
              onSetStatuses={(v) => {
                setStatuses(v);
                clearSelectedPickup();
                setForceResetStatuses(false);
              }}
              onSetPickupSearch={(v) => {
                setPickupSearch(v);
                clearSelectedPickup();
              }}
              forceResetStatuses={forceResetStatuses}
            />
          </div>
          <PickupList
            expiredOnly={expiredOnly}
            showFuture={showFuture}
            selectedPickupIds={selectedPickupIds}
            selectedPickupGroupId={selectedPickupGroupId}
            statuses={statuses}
            terminal={userState.activeTerminal?.id || null}
            pickupSearch={pickupSearch}
            onPickupSelect={(ids) => {
              setSelectedPickupIds(ids);
              setSelectedDriverId(null);
              setPickupDetailsNumber("");
              setDispatchToolsOpen(true);
            }}
            onGroupSelect={(id) => {
              setSelectedPickupGroupId(id);
              setDispatchToolsOpen(true);
            }}
            onShowPickupDetails={(id) => showPickupDetails(id)}
            height={pickupsListHeight}
            collapsed={dispatchToolsOpen}
          />
        </div>
        <PickupDetails
          isOpen={!!pickupDetailsNumber}
          pickup={pickupDetails}
          onClose={() => {
            setPickupDetailsNumber("");
            setShowReschedule(false);
          }}
          onUpdatePickup={onUpdatePickup}
          onReschedulePickup={onReschedulePickup}
          showReschedule={showReschedule}
          onShowPickup={showPickup}
          onEdit={onClickEditButton}
          isLoading={pickupDetailsState.requestStarted && pickupDetailsState.requestCreator === "GET_PICKUP"}
        />
        <PickupEdit
          isOpen={pickupEditMode}
          onClose={() => setPickupEditMode(false)}
          onSuccess={onEditPickup}
          pickup={pickupDetailsState.pickup}
        />
      </div>
    </ContentContainer>
  );
};

export default PickupAssignment;
